import { theme, useResponsive } from "@product/scmp-sdk";
import first from "lodash/first";
import last from "lodash/last";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { TakeoverAdSlot } from "~/components/advertisement/takeover-ad-slot";
import { ImageSize } from "~/components/article/article-leading/article-leading-images/enums";
import type { ArticleRenderChildProps } from "~/components/article/article-render";
import {
  useMobileInlineAdSlotIsDisabledFlag,
  useReduceWhitespaceByAvailableSpace,
} from "~/components/article/article-render/hooks";
import {
  RightAvailableSpaceTracker,
  WidgetsBottomAvailableSpaceTracker,
} from "~/components/article/article-render/opinion-article/styles";
import { ClientSideSuspense } from "~/components/common/client-side-suspense";
import type { InlineAdSlotRenderProps } from "~/components/content/content-schema-render/inline-ad-slot/hooks";
import type { ExtraProps as LinkExtraProps } from "~/components/content/content-schema-render/link";
import { useContentSchemaRenderLink } from "~/components/content/content-schema-render/link/hooks";
import { EntityLink } from "~/components/entity-link";
import { MagazinesStyleVariant } from "~/components/section/section-style/const";
import { StoryCardCarouselWidget } from "~/components/section/section-style/section-style-widgets/story-card-carousel-widget";
import type { styleArticleArticle$key } from "~/queries/__generated__/styleArticleArticle.graphql";

import { ArticleMoreOnThisList } from "./article-more-on-this-list";
import { DigitalArchiveLink } from "./digital-archive-link";
import {
  Author,
  AuthorDetail,
  Content,
  DesktopHeaderAdSlot,
  Follow,
  Headline,
  Leading,
  MobileContentHeaderAdSlot,
  MoreOnThis,
  Section,
  Social,
  StyleArticleContainer,
  SubHeadline,
  Topic,
  TopicSocialContainer,
} from "./styles";

type Props = {
  reference: styleArticleArticle$key;
} & ArticleRenderChildProps;

export const StyleArticle: FunctionComponent<Props> = ({ provider, reference }) => {
  const article = useFragment(
    graphql`
      fragment styleArticleArticle on Content {
        ...styleArticleAuthorBlockArticle
        ...reduceWhitespaceByAvailableSpaceArticle
        entityId
        sections {
          value {
            ...entityLink
            name
          }
        }
        ...articleMoreOnThisListStyleArticle
      }
    `,
    reference,
  );

  const isInlineAdSlotDisabled = useMobileInlineAdSlotIsDisabledFlag();
  const { reduceWhitespaceByAvailableSpaceSpec } = useReduceWhitespaceByAvailableSpace({
    reference: article,
    specs: {
      adSlotSize: 600,
      nativeAdSlotSize: 260,
      whitespaceAllowance: 100,
    },
    trackers: {
      right: RightAvailableSpaceTracker,
      widgetsBottom: WidgetsBottomAvailableSpaceTracker,
    },
  });

  const renderSection = () => {
    const lastSection = last(first(article.sections)?.value);
    if (!lastSection) return null;

    return (
      <Section>
        <EntityLink reference={lastSection}>{lastSection.name}</EntityLink>
      </Section>
    );
  };
  const linkExtraProps = useMemo<LinkExtraProps>(
    () => ({
      digitalArchiveLinkComponentRenderFunction: props => <DigitalArchiveLink {...props} />,
    }),
    [],
  );
  const { ComponentWithExtraProps: LinkWithExtraProps } =
    useContentSchemaRenderLink(linkExtraProps);

  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), false);
  const inlineAdSlots = useMemo<InlineAdSlotRenderProps>(
    () =>
      isDesktopUp
        ? {
            "0": {
              adUnit: "d_banner5",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250], "fluid"],
            },
            "1": {
              adUnit: "outstream1",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[300, 250], [640, 360], [640, 480], [480, 360], [480, 270], [1, 1], "fluid"],
            },
            "2": {
              adUnit: "d_banner2",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250], "fluid"],
            },
            "3": {
              adUnit: "d_lrec2",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[300, 250], "fluid"],
            },
            "4": {
              adUnit: "d_banner4",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250], "fluid"],
            },
            "5": {
              adUnit: "d_banner4",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250], "fluid"],
            },
            "6": {
              adUnit: "native4",
            },
            "7": {
              adUnit: "d_banner4",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250], "fluid"],
            },
          }
        : {
            "0": {
              adUnit: "m_banner5",
              breakpoint: theme.breakpoints.down("desktop"),
              sizes: [[300, 250], "fluid"],
            },
            "1": {
              adUnit: "outstream1",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [[300, 250], [320, 180], [1, 1], "fluid"],
            },
            "2": {
              adUnit: "m_lrec1",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
            },
            "3": {
              adUnit: "d_lrec2",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
            },
            "4": {
              adUnit: "m_lrec4d",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
            },
            "5": {
              adUnit: "m_lrec4e",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
            },
            "6": {
              adUnit: "native4",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
            },
            "7": {
              adUnit: "m_lrec4f",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
            },
          },
    [isDesktopUp, isInlineAdSlotDisabled],
  );

  return (
    <StyleArticleContainer>
      {reduceWhitespaceByAvailableSpaceSpec.widgetsBottom.trackerNode}
      {renderSection()}
      <Headline>{provider.headline({ variant: "magazines-style" })}</Headline>
      <Author reference={article} />
      <Leading>
        {provider.leading({
          responsiveDescriptionVariants: {
            desktopUp: "show",
            mobileUp: "hidden",
            tabletUp: "show",
          },
          responsiveImageVariants: { desktopUp: "normal", mobileUp: "square", tabletUp: "normal" },
          responsiveVariants: {
            desktopUp: ImageSize["1200x800"],
            mobileUp: ImageSize["768x768"],
            tabletUp: ImageSize["1020x680"],
          },
        })}
      </Leading>
      <TopicSocialContainer>
        <Follow>{provider.topicFollowWidget({ variant: MagazinesStyleVariant })}</Follow>
        <Social>{provider.shareWidget()}</Social>
      </TopicSocialContainer>
      <SubHeadline>{provider.subHeadline({ variant: MagazinesStyleVariant })}</SubHeadline>
      <Content>
        {provider.body({
          extraComponentMap: {
            a: LinkWithExtraProps,
            "inline-widget": null,
          },
          inlineAdSlots,
        })}
      </Content>
      <Topic>{provider.topics()}</Topic>
      <AuthorDetail>{provider.authorDetails()}</AuthorDetail>
      <MoreOnThis>
        <ArticleMoreOnThisList reference={article} />
      </MoreOnThis>
      <DesktopHeaderAdSlot>
        {provider.adslot({
          adUnit: "d_banner1",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: article.entityId,
            },
          },
          breakpoint: theme.breakpoints.up("desktop"),
          sizes: [[970, 250], [1, 1], [640, 360], [640, 480], [480, 270], "fluid"],
        })}
      </DesktopHeaderAdSlot>
      <MobileContentHeaderAdSlot>
        {provider.adslot({
          adUnit: "m_banner3",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: article.entityId,
            },
          },
          breakpoint: theme.breakpoints.down("desktop"),
          sizes: [[300, 250], [320, 100], [300, 50], [320, 50], "fluid"],
          withBackground: true,
          withLabel: true,
        })}
      </MobileContentHeaderAdSlot>
      <ClientSideSuspense>
        <TakeoverAdSlot />
        <StoryCardCarouselWidget />
      </ClientSideSuspense>
    </StyleArticleContainer>
  );
};

StyleArticle.displayName = "StyleArticle";
